import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { candidateApi, recruiterApi } from '../services/api'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/Badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Users, AlertCircle, Mail, Phone, Calendar, Briefcase, Trash2, Loader2, X, Send, FileText, Pencil, Save, Eye } from 'lucide-react'

interface Candidate {
  id: number
  uuid: string
  recruiter_id: number
  name: string
  email: string | null
  phone: string | null
  years_of_experience: number | null
  cv_file_path: string
  extraction_status: string
  extraction_errors: string | null
  properties?: string | null
  created_at: string
}

interface Form {
  id: number
  uuid: string
  job_description: string | null
  questions: Array<any>
  created_at: string
  updated_at: string
}

export default function CandidatesPage() {
  const configuredFrontendBaseUrl = (
    (import.meta.env.FRONTEND_BASE_URL as string | undefined)
    || (import.meta.env.VITE_PUBLIC_FRONTEND_URL as string | undefined)
  )?.trim()
  const { recruiter } = useAuth()
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [candidateToDelete, setCandidateToDelete] = useState<Candidate | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null)
  const [selectedCandidates, setSelectedCandidates] = useState<Set<number>>(new Set())
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  const [showEmailDialog, setShowEmailDialog] = useState(false)
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [forms, setForms] = useState<Form[]>([])
  const [selectedTemplateFormUuid, setSelectedTemplateFormUuid] = useState<string>('')
  const [frontendBaseUrl, setFrontendBaseUrl] = useState<string>('')
  const [isLoadingForms, setIsLoadingForms] = useState(false)
  const [lastGeneratedLinks, setLastGeneratedLinks] = useState<Array<{ candidate_name?: string | null; candidate_email?: string | null; sent_form_uuid?: string; url?: string | null }>>([])
  const [extractingCandidateId, setExtractingCandidateId] = useState<number | null>(null)
  const [editingCandidateId, setEditingCandidateId] = useState<number | null>(null)
  const [editingForm, setEditingForm] = useState<{ name: string; email: string; phone: string }>({
    name: '',
    email: '',
    phone: '',
  })
  const [isUpdatingCandidate, setIsUpdatingCandidate] = useState(false)
  const [viewingCvCandidateId, setViewingCvCandidateId] = useState<number | null>(null)

  const fetchCandidates = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await candidateApi.getCandidates()
      
      if (response.status === 'success') {
        setCandidates(response.candidates)
      } else {
        setError('שגיאה בטעינת המועמדים')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה בטעינת המועמדים')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCandidates()
  }, [recruiter])

  const fetchForms = async () => {
    try {
      setIsLoadingForms(true)
      const response = await recruiterApi.getForms()
      
      if (response.status === 'success') {
        setForms(response.forms)
      }
    } catch (err: any) {
      console.error('Failed to fetch forms:', err)
    } finally {
      setIsLoadingForms(false)
    }
  }

  useEffect(() => {
    if (showEmailDialog) {
      fetchForms()
      const origin = window.location.origin
      const hostname = window.location.hostname
      const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0'
      setFrontendBaseUrl(configuredFrontendBaseUrl || (isLocalHost ? '' : origin))
    }
  }, [showEmailDialog, recruiter, configuredFrontendBaseUrl])

  const handleDeleteClick = (candidate: Candidate) => {
    setCandidateToDelete(candidate)
  }

  const handleDeleteConfirm = async () => {
    if (!candidateToDelete) return

    try {
      setIsDeleting(true)
      setError(null)
      await candidateApi.deleteCandidate(candidateToDelete.id)
      setCandidateToDelete(null)
      // Refresh the candidates list
      await fetchCandidates()
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה במחיקת המועמד')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setCandidateToDelete(null)
  }

  const handleSelectCandidate = (candidateId: number) => {
    setSelectedCandidates(prev => {
      const newSet = new Set(prev)
      if (newSet.has(candidateId)) {
        newSet.delete(candidateId)
      } else {
        newSet.add(candidateId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    if (selectedCandidates.size === candidates.length) {
      setSelectedCandidates(new Set())
    } else {
      setSelectedCandidates(new Set(candidates.map(c => c.id)))
    }
  }

  const handleBulkDelete = () => {
    if (selectedCandidates.size === 0) return
    setCandidateToDelete({ id: 0 } as Candidate) // Use a flag to indicate bulk delete
  }

  const handleBulkDeleteConfirm = async () => {
    if (selectedCandidates.size === 0) return

    try {
      setIsBulkDeleting(true)
      setError(null)
      const candidateIds = Array.from(selectedCandidates)
      await candidateApi.bulkDeleteCandidates(candidateIds)
      setSelectedCandidates(new Set())
      setCandidateToDelete(null)
      await fetchCandidates()
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה במחיקת המועמדים')
    } finally {
      setIsBulkDeleting(false)
    }
  }

  const handleSendEmail = () => {
    if (selectedCandidates.size === 0) return
    setLastGeneratedLinks([])
    setShowEmailDialog(true)
  }

  const handleEmailSend = async () => {
    const trimmedBaseUrl = frontendBaseUrl.trim()
    const currentHost = window.location.hostname.toLowerCase()
    const isRunningLocally = currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost === '0.0.0.0'
    if (trimmedBaseUrl) {
      try {
        const parsedUrl = new URL(trimmedBaseUrl)
        const host = parsedUrl.hostname.toLowerCase()
        const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0'
        const isHttp = parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'

        if (!isHttp || (isLocalHost && !isRunningLocally)) {
          setError('כתובת בסיס לטופס חייבת להיות מסוג http/https, ובסביבת פרודקשן לא יכולה להיות localhost')
          return
        }
      } catch {
        setError('כתובת בסיס לטופס אינה תקינה')
        return
      }
    }

    try {
      setIsSendingEmail(true)
      setError(null)
      const candidateIds = Array.from(selectedCandidates)
      const defaultEmailSubject = 'דוח הערכת מועמד'
      const emailBodyForSend = selectedTemplateFormUuid
        ? 'הי :) מצורפת הערכת המועמד שלך.\n\n<br><br>{{FORM_LINK}}'
        : 'הי :) מצורפת הערכת המועמד שלך.'
      const response = await candidateApi.bulkEmailCandidates(
        candidateIds,
        defaultEmailSubject,
        emailBodyForSend,
        selectedTemplateFormUuid || undefined,
        frontendBaseUrl || undefined
      )
      
      if (response.status === 'success') {
        setLastGeneratedLinks(response.generated_links || [])
        setShowEmailDialog(false)
        setSelectedCandidates(new Set())
        if (response.failed_count > 0) {
          setError(`נשלחו ${response.sent_count} אימיילים, ${response.failed_count} נכשלו`)
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה בשליחת האימיילים')
    } finally {
      setIsSendingEmail(false)
    }
  }

  const handleEmailCancel = () => {
    setShowEmailDialog(false)
    setSelectedTemplateFormUuid('')
  }

  const handleExtractCv = async (candidate: Candidate) => {
    try {
      setExtractingCandidateId(candidate.id)
      setError(null)
      const response = await candidateApi.extractCv(candidate.id)
      
      if (response.status === 'success') {
        // Refresh candidates to get updated properties
        await fetchCandidates()
        // Show success message (you could add a toast notification here)
        setError(null)
      } else {
        setError(response.message || 'שגיאה בחילוץ קורות החיים')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה בחילוץ קורות החיים')
    } finally {
      setExtractingCandidateId(null)
    }
  }

  const startEditingCandidate = (candidate: Candidate) => {
    setEditingCandidateId(candidate.id)
    setEditingForm({
      name: candidate.name || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
    })
  }

  const cancelEditingCandidate = () => {
    setEditingCandidateId(null)
    setEditingForm({ name: '', email: '', phone: '' })
  }

  const saveEditedCandidate = async (candidateId: number) => {
    if (!editingForm.name.trim()) {
      setError('שם המועמד הוא שדה חובה')
      return
    }

    try {
      setIsUpdatingCandidate(true)
      setError(null)
      await candidateApi.updateCandidate(
        candidateId,
        editingForm.name.trim(),
        editingForm.email,
        editingForm.phone
      )

      setCandidates((prev) =>
        prev.map((candidate) =>
          candidate.id === candidateId
            ? {
                ...candidate,
                name: editingForm.name.trim(),
                email: editingForm.email.trim() || null,
                phone: editingForm.phone.trim() || null,
              }
            : candidate
        )
      )
      cancelEditingCandidate()
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה בעדכון פרטי המועמד')
    } finally {
      setIsUpdatingCandidate(false)
    }
  }

  const handleViewCv = async (candidate: Candidate) => {
    try {
      setViewingCvCandidateId(candidate.id)
      setError(null)
      const cvBlob = await candidateApi.getCandidateCv(candidate.id)
      const blobUrl = window.URL.createObjectURL(cvBlob)
      window.open(blobUrl, '_blank', 'noopener,noreferrer')
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl)
      }, 60_000)
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה בטעינת קובץ קורות החיים')
    } finally {
      setViewingCvCandidateId(null)
    }
  }

  const isAllSelected = candidates.length > 0 && selectedCandidates.size === candidates.length
  const isIndeterminate = selectedCandidates.size > 0 && selectedCandidates.size < candidates.length
  const trimmedFrontendBaseUrl = frontendBaseUrl.trim()
  const currentHost = window.location.hostname.toLowerCase()
  const isRunningLocally = currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost === '0.0.0.0'
  const frontendBaseUrlValidationMessage = (() => {
    if (!trimmedFrontendBaseUrl) return ''
    try {
      const parsedUrl = new URL(trimmedFrontendBaseUrl)
      const host = parsedUrl.hostname.toLowerCase()
      const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0'
      const isHttp = parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
      if (!isHttp) return 'הכתובת חייבת להתחיל ב-http או https'
      if (isLocalHost && !isRunningLocally) return 'localhost לא נגיש מהנייד. השתמש בכתובת ציבורית.'
      return ''
    } catch {
      return 'פורמט כתובת לא תקין'
    }
  })()
  const selectedTestUrlPreview =
    trimmedFrontendBaseUrl && selectedTemplateFormUuid && !frontendBaseUrlValidationMessage
      ? `${trimmedFrontendBaseUrl.replace(/\/$/, '')}/candidate/<random_uuid_generated_on_send>`
      : ''

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    } catch {
      return dateString
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge variant="default">הצליח</Badge>
      case 'partial':
        return <Badge variant="default">חלקי</Badge>
      case 'failed':
        return <Badge variant="destructive">נכשל</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  if (isLoading) {
    return (
      <div className="bg-background p-6 flex items-center justify-center min-h-[calc(100vh-96px)]">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="bg-background p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">מועמדים</h1>
          <p className="text-muted-foreground mt-1">
            רשימת כל המועמדים במערכת
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-muted-foreground" />
          <span className="text-lg font-semibold">{candidates.length} מועמדים</span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {lastGeneratedLinks.length > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <p className="font-medium text-green-800">קישורי מבחן שנוצרו למועמדים</p>
              <div className="space-y-2">
                {lastGeneratedLinks.map((item, index) => (
                  <div key={`${item.sent_form_uuid || item.url || index}`} className="rounded-md border border-green-200 bg-white p-3">
                    <p className="text-sm text-slate-700">
                      {item.candidate_name || 'מועמד'}
                      {item.candidate_email ? ` (${item.candidate_email})` : ''}
                    </p>
                    {item.sent_form_uuid && (
                      <p className="mt-1 text-xs text-slate-600 break-all">UUID: {item.sent_form_uuid}</p>
                    )}
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 block text-sm text-blue-700 underline break-all"
                      >
                        {item.url}
                      </a>
                    ) : (
                      <p className="mt-1 text-xs text-amber-700">
                        לא נבנה URL מלא (FRONTEND_BASE_URL חסר), אבל ה-UUID נוצר ונשמר.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Candidates Table */}
      {candidates.length === 0 && !error ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium text-muted-foreground">
                אין מועמדים במערכת
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                העלה קורות חיים כדי להוסיף מועמדים
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="flex max-h-[calc(100vh-14rem)] flex-col overflow-hidden">
          <CardHeader className="shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle>רשימת מועמדים</CardTitle>
              {selectedCandidates.size > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="default">{selectedCandidates.size} מועמדים נבחרו</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    מחק נבחרים
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSendEmail}
                    disabled={isSendingEmail}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    שלח אימייל
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="min-h-0 overflow-auto">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-right p-4 font-semibold w-12">
                      <Checkbox
                        checked={isIndeterminate ? ('indeterminate' as any) : isAllSelected}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="text-right p-4 font-semibold">שם</th>
                    <th className="text-right p-4 font-semibold">אימייל</th>
                    <th className="text-right p-4 font-semibold">טלפון</th>
                    <th className="text-right p-4 font-semibold">שנות ניסיון</th>
                    <th className="text-right p-4 font-semibold">סטטוס</th>
                    <th className="text-right p-4 font-semibold">תאריך הוספה</th>
                    <th className="text-right p-4 font-semibold w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((candidate) => (
                    <tr 
                      key={candidate.id} 
                      className="border-b hover:bg-muted/50 transition-colors"
                      onMouseEnter={() => setHoveredRowId(candidate.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                    >
                      <td className="p-4">
                        <Checkbox
                          checked={selectedCandidates.has(candidate.id)}
                          onCheckedChange={() => handleSelectCandidate(candidate.id)}
                        />
                      </td>
                      <td className="p-4">
                        {editingCandidateId === candidate.id ? (
                          <Input
                            value={editingForm.name}
                            onChange={(e) => setEditingForm((prev) => ({ ...prev, name: e.target.value }))}
                            placeholder="שם מלא"
                            disabled={isUpdatingCandidate}
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{candidate.name}</span>
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        {editingCandidateId === candidate.id ? (
                          <Input
                            value={editingForm.email}
                            onChange={(e) => setEditingForm((prev) => ({ ...prev, email: e.target.value }))}
                            placeholder="example@company.com"
                            dir="ltr"
                            disabled={isUpdatingCandidate}
                          />
                        ) : (
                          <>
                            {candidate.email ? (
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                                <span>{candidate.email}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="p-4">
                        {editingCandidateId === candidate.id ? (
                          <Input
                            value={editingForm.phone}
                            onChange={(e) => setEditingForm((prev) => ({ ...prev, phone: e.target.value }))}
                            placeholder="05XXXXXXXX"
                            dir="ltr"
                            disabled={isUpdatingCandidate}
                          />
                        ) : (
                          <>
                            {candidate.phone ? (
                              <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-muted-foreground" />
                                <span>{candidate.phone}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="p-4">
                        {candidate.years_of_experience !== null ? (
                          <div className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4 text-muted-foreground" />
                            <span>{candidate.years_of_experience} שנים</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        {getStatusBadge(candidate.extraction_status)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span>{formatDate(candidate.created_at)}</span>
                        </div>
                      </td>
                      <td className="p-4 w-16">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 transition-opacity duration-200 ${
                              hoveredRowId === candidate.id
                                ? 'opacity-100 visible text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                : 'opacity-0 invisible'
                            }`}
                            onClick={() => handleViewCv(candidate)}
                            disabled={viewingCvCandidateId === candidate.id}
                            title="צפה בקורות חיים"
                          >
                            {viewingCvCandidateId === candidate.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 transition-opacity duration-200 ${
                              hoveredRowId === candidate.id
                                ? 'opacity-100 visible text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                : 'opacity-0 invisible'
                            }`}
                            onClick={() => handleExtractCv(candidate)}
                            disabled={extractingCandidateId === candidate.id}
                            title="חלץ קורות חיים ל-JSON"
                          >
                            {extractingCandidateId === candidate.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <FileText className="h-4 w-4" />
                            )}
                          </Button>
                          {editingCandidateId === candidate.id ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                onClick={() => saveEditedCandidate(candidate.id)}
                                disabled={isUpdatingCandidate}
                                title="שמור שינויים"
                              >
                                {isUpdatingCandidate ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Save className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                onClick={cancelEditingCandidate}
                                disabled={isUpdatingCandidate}
                                title="בטל עריכה"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className={`h-8 w-8 transition-opacity duration-200 ${
                                hoveredRowId === candidate.id
                                  ? 'opacity-100 visible text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                  : 'opacity-0 invisible'
                              }`}
                              onClick={() => startEditingCandidate(candidate)}
                              disabled={isUpdatingCandidate}
                              title="ערוך פרטי מועמד"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 transition-opacity duration-200 ${
                              hoveredRowId === candidate.id
                                ? 'opacity-100 visible text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                : 'opacity-0 invisible'
                            }`}
                            onClick={() => handleDeleteClick(candidate)}
                            title="מחק מועמד"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      {candidateToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>
                {candidateToDelete.id === 0 ? 'מחיקת מועמדים' : 'מחיקת מועמד'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {candidateToDelete.id === 0 ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    האם אתה בטוח שברצונך למחוק את {selectedCandidates.size} המועמדים הנבחרים?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    פעולה זו לא ניתנת לביטול.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    האם אתה בטוח שברצונך למחוק את המועמד <strong>{candidateToDelete.name}</strong>?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    פעולה זו לא ניתנת לביטול.
                  </p>
                </>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleDeleteCancel}
                  disabled={isDeleting || isBulkDeleting}
                >
                  ביטול
                </Button>
                <Button
                  variant="outline"
                  onClick={candidateToDelete.id === 0 ? handleBulkDeleteConfirm : handleDeleteConfirm}
                  disabled={isDeleting || isBulkDeleting}
                  className="text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900"
                >
                  {(isDeleting || isBulkDeleting) ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      מוחק...
                    </>
                  ) : (
                    'מחק'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Email Dialog */}
      {showEmailDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>שלח אימייל למועמדים נבחרים</CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleEmailCancel}
                  disabled={isSendingEmail}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  נבחרו {selectedCandidates.size} מועמדים:
                </p>
                <div className="max-h-32 overflow-y-auto border rounded-md p-2">
                  {candidates
                    .filter(c => selectedCandidates.has(c.id))
                    .map(candidate => (
                      <div key={candidate.id} className="flex items-center gap-2 py-1">
                        <Users className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">
                          {candidate.name}
                          {candidate.email && (
                            <span className="text-muted-foreground"> ({candidate.email})</span>
                          )}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
              {frontendBaseUrlValidationMessage && (
                <div>
                  <p className="text-xs text-red-600">{frontendBaseUrlValidationMessage}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium mb-2 block">בחר טופס מקור (Template) (אופציונלי)</label>
                {isLoadingForms ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    טוען טפסים...
                  </div>
                ) : forms.length === 0 ? (
                  <p className="text-sm text-muted-foreground">אין טפסים זמינים</p>
                ) : (
                  <select
                    value={selectedTemplateFormUuid}
                    onChange={(e) => setSelectedTemplateFormUuid(e.target.value)}
                    disabled={isSendingEmail}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  >
                    <option value="">-- בחר טופס --</option>
                    {forms.map((form) => (
                      <option key={form.uuid} value={form.uuid}>
                        {form.job_description 
                          ? `${form.job_description.substring(0, 50)}${form.job_description.length > 50 ? '...' : ''} (${form.uuid.substring(0, 8)}...)`
                          : `טופס ${form.uuid.substring(0, 8)}...`}
                      </option>
                    ))}
                  </select>
                )}
                {selectedTestUrlPreview ? (
                  <div className="mt-2 rounded-md border bg-muted/40 p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">תצוגת פורמט קישור המבחן</p>
                    <p className="block text-sm text-slate-700 break-all">
                      {selectedTestUrlPreview}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      הקישור האמיתי לכל מועמד ייווצר רק אחרי שליחה ויופיע בכרטיס "קישורי מבחן שנוצרו למועמדים".
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">
                    בחר טופס מקור והכנס כתובת בסיס תקינה כדי להציג את הקישור המלא. לכל מועמד ייווצר UUID ייחודי בעת השליחה.
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleEmailCancel}
                  disabled={isSendingEmail}
                >
                  ביטול
                </Button>
                <Button
                  onClick={handleEmailSend}
                  disabled={isSendingEmail || !!frontendBaseUrlValidationMessage}
                >
                  {isSendingEmail ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      שולח...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      שלח
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
